import { Component } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";
import { trackEvent, langFromPath } from "@/analytics/ga";

/**
 * Declarative React error boundary (Security & Stability initiative, P1).
 *
 * React unmounts the WHOLE tree when a render/lifecycle error goes uncaught,
 * which is what produced the blank screen on `/auth/login`. A boundary
 * confines the failure to the nearest wrapped subtree and renders an
 * accessible, bilingual fallback instead.
 *
 * Design notes
 *   - Class component on purpose: `getDerivedStateFromError` /
 *     `componentDidCatch` have no hook equivalent.
 *   - Zero context dependencies. The boundary may sit ABOVE
 *     `LanguageProvider` (app scope), so the locale is derived from the URL
 *     exactly like `analytics/ga.js` does.
 *   - `resetKey`: when it changes (e.g. the pathname), a previously caught
 *     error is cleared so navigating away from a broken page recovers
 *     automatically without a full reload.
 *   - Telemetry is deliberately minimal and PII-free: only the boundary
 *     scope and the error's constructor name are sent — never the message
 *     or stack (they can contain user input).
 *
 * Usage
 *   <ErrorBoundary scope="section">…</ErrorBoundary>
 *   <RouteErrorBoundary scope="app">…</RouteErrorBoundary>   // inside Router
 */

const COPY = {
  ar: {
    title: "حدث خطأ غير متوقّع",
    body: "تعذّر عرض هذا الجزء من الصفحة. يمكنك المحاولة مجددًا أو العودة إلى الصفحة الرئيسية.",
    retry: "حاول مجددًا",
    reload: "إعادة تحميل الصفحة",
    home: "الصفحة الرئيسية",
    aria: "رسالة خطأ",
  },
  en: {
    title: "Something went wrong",
    body: "This part of the page could not be displayed. You can try again or go back to the home page.",
    retry: "Try again",
    reload: "Reload page",
    home: "Home",
    aria: "Error message",
  },
};

function currentLang() {
  if (typeof window === "undefined") return "en";
  return langFromPath(window.location.pathname);
}

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
    this.reload = this.reload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error: error || new Error("Unknown render error") };
  }

  componentDidCatch(error, info) {
    // Keep the full detail in the console for engineers…
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", this.props.scope || "section", error, info?.componentStack);
    // …but only a safe, non-identifying signal in analytics.
    try {
      trackEvent("app_error", {
        scope: this.props.scope || "section",
        error_name: (error && error.name) || "Error",
      });
    } catch {
      /* analytics must never throw into the fallback */
    }
    if (typeof this.props.onError === "function") {
      try {
        this.props.onError(error, info);
      } catch {
        /* ignore */
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Route (or any caller-provided key) changed → give the subtree a
    // fresh chance to render.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  reset() {
    this.setState({ error: null });
  }

  reload() {
    if (typeof window !== "undefined") window.location.reload();
  }

  render() {
    if (!this.state.error) return this.props.children;

    if (typeof this.props.fallback === "function") {
      return this.props.fallback({ error: this.state.error, reset: this.reset });
    }

    const lang = currentLang();
    const c = COPY[lang] || COPY.en;
    const isApp = this.props.scope === "app";
    const home = lang === "ar" ? "/ar" : "/";

    return (
      <div
        role="alert"
        aria-live="assertive"
        aria-label={c.aria}
        dir={lang === "ar" ? "rtl" : "ltr"}
        className={
          isApp
            ? "min-h-screen flex items-center justify-center px-4 py-10 bg-white dark:bg-[#0e0e12]"
            : "sas-container py-12 sm:py-16"
        }
        data-testid={`error-boundary-${this.props.scope || "section"}`}
      >
        <div className="w-full max-w-md mx-auto rounded-2xl border border-[color:var(--sas-line)] bg-white dark:bg-[#15151b] p-7 sm:p-8">
          <div
            className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-4"
            style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
            aria-hidden
          >
            <AlertTriangle size={18} />
          </div>
          <h2 className="sas-h3" data-testid="error-boundary-title">
            {c.title}
          </h2>
          <p className="sas-body mt-2 text-[14px]" data-testid="error-boundary-body">
            {c.body}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={isApp ? this.reload : this.reset}
              className="sas-btn sas-btn-primary justify-center"
              data-testid="error-boundary-retry"
            >
              <RotateCcw size={15} aria-hidden />
              <span>{isApp ? c.reload : c.retry}</span>
            </button>
            <a
              href={home}
              className="sas-btn sas-btn-ghost justify-center"
              data-testid="error-boundary-home"
            >
              <Home size={15} aria-hidden />
              <span>{c.home}</span>
            </a>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * Convenience wrapper that resets the boundary whenever the route changes.
 * Must be rendered inside a Router (uses `useLocation`).
 */
export function RouteErrorBoundary({ scope = "section", children }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary scope={scope} resetKey={pathname}>
      {children}
    </ErrorBoundary>
  );
}
